import {
  Button,
  Col,
  Form,
  InputNumber,
  Layout,
  message,
  Row,
  Select,
  Table,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";
import { formatNumber, formatString, navigateTo } from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router";

interface DiscountFormProps {
  add?: boolean;
  edit?: boolean;
  review?: boolean;
}

const DiscountForm: React.FC<DiscountFormProps> = ({ add, edit, review }) => {
  const [loading,            setLoading]        = useState("");
  const [errorMessage,       setErrorMessage]   = useState("");
  const [successMessage,     setSuccessMessage] = useState("");
  const [companies,          setCompanies]      = useState<any[]>([]);
  const [selectedCompany,    setSelectedCompany]= useState<any>(null);
  const [rwtList,            setRwtList]        = useState<any[]>([]);
  const [rwtLoading,         setRwtLoading]     = useState(false);
  const [discountValues,     setDiscountValues] = useState<any>({});
  const [form]                                  = Form.useForm();

  const { id: discount_id } = useParams<{ id: string }>();

  const isReadOnly = review;

  function clearFields() {
    form.resetFields();
    setSelectedCompany(null);
    setRwtList([]);
    setDiscountValues({});
  }

  const hasDiscount = Object.values(discountValues).some((v: any) => v > 0);

  const rwtColumns = [
    {
      title: "RWT Number",
      dataIndex: "rwt_no",
      key: "rwt_no",
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right" as any,
      render: (value: any) => formatNumber(value),
    },
    {
      title: "Discount Amount (₱)",
      key: "discount_amount",
      width: 220,
      render: (_: any, record: any) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          value={discountValues[record.id] ?? 0}
          disabled={isReadOnly}
          onChange={(val) =>
            setDiscountValues((prev: any) => ({ ...prev, [record.id]: val ?? 0 }))
          }
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(value) => value?.replace(/,/g, "") as any}
        />
      ),
    },
  ];

  async function submitForm() {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    if (!hasDiscount) {
      message.warning("Please enter at least one discount amount.");
      return;
    }

    const items = Object.entries(discountValues)
      .filter(([_, amount]) => (amount as number) > 0)
      .map(([transaction_id, discount_amount]) => ({ transaction_id, discount_amount }));

    const payload: any = { customer_id: selectedCompany, items };
    if (edit) payload.discount_id = discount_id;

    setLoading("submit");
    try {
      const response = await postRequest(
        add ? "discounts/create" : "discounts/update",
        payload,
        true
      );
      if (response.success) {
        setSuccessMessage(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          setSuccessMessage("");
          navigateTo("/discount");
        }, 1500);
      } else {
        setErrorMessage(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            `Failed to ${add ? "create" : "update"} discount.`
        );
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function fetchCompanies() {
    const response = await getRequest("companies/get", {}, true);
    if (response.success) setCompanies(response.data.data);
  }

  async function fetchUnpaidRWTs(companyId: number) {
    setRwtLoading(true);
    setRwtList([]);
    try {
      const response = await getRequest(
        "daily_transactions/get",
        { company_id: companyId, status: "pending" },
        true
      );
      if (response.success) {
        setRwtList(response.data.data);
      } else {
        setRwtList([]);
      }
    } catch {
      setErrorMessage("Error loading RWT records. Please try again.");
    } finally {
      setRwtLoading(false);
    }
  }

  async function fetchDiscountData() {
    if (!discount_id) return;
    setLoading("fetch");
    try {
      const response = await getRequest("discounts/get", { discount_id }, true);
      if (response.success) {
        const data = response.data.data[0];
        setSelectedCompany(data.customer_id);
        form.setFieldsValue({ customer_id: Number(data.customer_id) });
        await fetchUnpaidRWTs(data.customer_id);
        if (data.items?.length > 0) {
          const mapped: any = {};
          data.items.forEach((item: any) => {
            mapped[item.transaction_id] = Number(item.discount_amount);
          });
          setDiscountValues(mapped);
        }
      }
    } catch {
      setErrorMessage("Failed to load discount data.");
    } finally {
      setLoading("");
    }
  }

  useEffect(() => {
    fetchCompanies();
    if (edit || review) fetchDiscountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTitle = add    ? "Add Discount"
    : edit   ? "Edit Discount"
    : review ? "Review Discount"
    :          "View Discount";

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={pageTitle}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>

          {loading !== "fetch" && (
            <div className="form-container w-full">
              <Col span={24}>
                <Form form={form} layout="vertical">
                  <Row className="w-full" gutter={[10, 10]}>

                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Company"
                        name="customer_id"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select a company"
                          disabled={isReadOnly || edit}
                          optionFilterProp="label"
                          onChange={(val) => {
                            setSelectedCompany(val ?? null);
                            setRwtList([]);
                            setDiscountValues({});
                            if (val) fetchUnpaidRWTs(val);
                          }}
                          options={companies.map((c: any) => ({
                            value: Number(c.id),
                            label: c.name,
                          }))}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <span className="edit-label mb-2">
                        <label className="text-red-500 me-1">*</label>Unpaid RWT Records:
                      </span>
                      <div className="overflow-auto max-h-[500px] mt-2">
                        <Table
                          dataSource={rwtList}
                          loading={rwtLoading}
                          rowKey="id"
                          size="small"
                          bordered
                          pagination={false}
                          columns={rwtColumns}
                          locale={{
                            emptyText: selectedCompany
                              ? "No unpaid RWT records found."
                              : "Select a company to load RWT records.",
                          }}
                        />
                      </div>
                    </Col>

                    <Col xs={24} className="mt-5 flex justify-end gap-3">
                      <Button
                        onClick={() => {
                          navigateTo("/discount");
                          clearFields();
                        }}
                      >
                        {isReadOnly ? "Close" : "Cancel"}
                      </Button>

                      {(add || edit) && (
                        <Button
                          type="primary"
                          loading={loading === "submit"}
                          disabled={rwtList.length === 0 || rwtLoading}
                          onClick={submitForm}
                        >
                          {add ? "Submit" : "Update"}
                        </Button>
                      )}
                    </Col>

                  </Row>
                </Form>
              </Col>
            </div>
          )}
        </Row>
      </Layout>

      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />
      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default DiscountForm;